import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, Tag, Row, Col, Statistic, message, Space, Tabs, Alert, Badge, Drawer, Divider } from 'antd';
import { PlusOutlined, WarningOutlined, ExclamationCircleOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import apiService from 'services/api/apiService';
const { TextArea } = Input;

const incidentColors: Record<string,string> = { verbal_warning:'default', written_warning:'warning', final_warning:'orange', suspension:'error', termination:'red', misconduct:'volcano', poor_performance:'gold', attendance:'cyan', insubordination:'magenta', other:'default' };
const statusColors: Record<string,string> = { open:'processing', under_investigation:'warning', resolved:'success', appealed:'orange', closed:'default' };
const grievanceColors: Record<string,string> = { filed:'processing', acknowledged:'blue', under_investigation:'warning', mediation:'orange', resolved:'success', closed:'default', escalated:'error' };

const DisciplinaryManagement: React.FC = () => {
  const [cases, setCases] = useState<any[]>([]);
  const [grievances, setGrievances] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [grievanceSummary, setGrievanceSummary] = useState<any>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('disciplinary');
  const [caseModal, setCaseModal] = useState(false);
  const [grievanceModal, setGrievanceModal] = useState(false);
  const [detailDrawer, setDetailDrawer] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [caseForm] = Form.useForm();
  const [grievanceForm] = Form.useForm();

  useEffect(() => { fetchAll(); }, [activeTab]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [er, cr, gr, cs, gs] = await Promise.all([
        apiService.getEmployees(),
        apiService.getDisciplinaryCases('disciplinary'),
        apiService.getDisciplinaryCases('grievance'),
        apiService.getDisciplinarySummary('disciplinary'),
        apiService.getDisciplinarySummary('grievance'),
      ]);
      if (er.success) setEmployees(er.data || []);
      if (cr.success) setCases(cr.data || []);
      if (gr.success) setGrievances(gr.data || []);
      if (cs.success) setSummary(cs.data);
      if (gs.success) setGrievanceSummary(gs.data);
    } finally { setLoading(false); }
  };

  const handleCaseSubmit = async (values: any) => {
    const r = await apiService.createDisciplinaryCase({ ...values, type: 'disciplinary' });
    if (r.success) { message.success('Case created'); setCaseModal(false); caseForm.resetFields(); fetchAll(); }
    else message.error(r.error || 'Failed');
  };

  const handleGrievanceSubmit = async (values: any) => {
    const r = await apiService.createDisciplinaryCase({ ...values, type: 'grievance' });
    if (r.success) { message.success('Grievance filed'); setGrievanceModal(false); grievanceForm.resetFields(); fetchAll(); }
    else message.error(r.error || 'Failed');
  };

  const updateStatus = async (id: number, status: string, type: string) => {
    const r = await apiService.updateDisciplinaryCase({ id, status, type });
    if (r.success) { message.success('Status updated'); fetchAll(); }
    else message.error(r.error || 'Failed');
  };

  const empOptions = employees.map(e => <Select.Option key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.department}</Select.Option>);

  const disciplinaryTab = (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={4}><Card><Statistic title="Total Cases" value={summary?.total || 0} /></Card></Col>
        <Col span={4}><Card><Statistic title="Open" value={summary?.open_cases || 0} valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
        <Col span={4}><Card><Statistic title="Terminations" value={summary?.terminations || 0} valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
        <Col span={4}><Card><Statistic title="Suspensions" value={summary?.suspensions || 0} valueStyle={{ color: '#faad14' }} /></Card></Col>
        <Col span={4}><Card><Statistic title="Written Warnings" value={summary?.written_warnings || 0} /></Card></Col>
        <Col span={4}><Card><Statistic title="Appeals" value={summary?.appeals || 0} valueStyle={{ color: '#1890ff' }} /></Card></Col>
      </Row>
      <Card extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setCaseModal(true)}>New Case</Button>}>
        <Table dataSource={cases} rowKey="id" loading={loading} pagination={{ pageSize: 10 }}
          columns={[
            { title: 'Case ID', dataIndex: 'case_id', render: (v: string) => <code style={{ fontSize: 11 }}>{v}</code>, width: 110 },
            { title: 'Employee', dataIndex: 'employee_name', render: (v: string, r: any) => <><div style={{ fontWeight: 600 }}>{v}</div><div style={{ fontSize: 11, color: '#999' }}>{r.department}</div></> },
            { title: 'Incident Type', dataIndex: 'incident_type', render: (v: string) => <Tag color={incidentColors[v] || 'default'}>{v?.replace(/_/g,' ')}</Tag> },
            { title: 'Action Taken', dataIndex: 'action_taken', render: (v: string) => <Tag color={incidentColors[v] || 'default'}>{v?.replace(/_/g,' ')}</Tag> },
            { title: 'Incident Date', dataIndex: 'incident_date' },
            { title: 'Hearing Date', dataIndex: 'hearing_date', render: (v: string) => v || '-' },
            { title: 'Status', dataIndex: 'status', render: (v: string) => <Badge status={statusColors[v] as any} text={v?.replace(/_/g,' ')} /> },
            { title: 'Actions', render: (_: any, r: any) => (
              <Space>
                <Button size="small" icon={<EyeOutlined />} onClick={() => { setSelected({ ...r, _type: 'disciplinary' }); setDetailDrawer(true); }} />
                {r.status === 'open' && <Button size="small" onClick={() => updateStatus(r.id, 'under_investigation', 'disciplinary')}>Investigate</Button>}
                {r.status === 'under_investigation' && <Button size="small" type="primary" onClick={() => updateStatus(r.id, 'resolved', 'disciplinary')}>Resolve</Button>}
              </Space>
            )},
          ]} />
      </Card>
    </div>
  );

  const grievanceTab = (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}><Card><Statistic title="Total Grievances" value={grievanceSummary?.total || 0} /></Card></Col>
        <Col span={6}><Card><Statistic title="Filed" value={grievanceSummary?.filed || 0} valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
        <Col span={6}><Card><Statistic title="Under Investigation" value={grievanceSummary?.investigating || 0} valueStyle={{ color: '#faad14' }} /></Card></Col>
        <Col span={6}><Card><Statistic title="Resolved" value={grievanceSummary?.resolved || 0} valueStyle={{ color: '#52c41a' }} /></Card></Col>
      </Row>
      <Alert message="All grievances are treated confidentially. Employees may file anonymously." type="info" showIcon style={{ marginBottom: 16 }} />
      <Card extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setGrievanceModal(true)}>File Grievance</Button>}>
        <Table dataSource={grievances} rowKey="id" loading={loading} pagination={{ pageSize: 10 }}
          columns={[
            { title: 'Grievance ID', dataIndex: 'grievance_id', render: (v: string) => <code style={{ fontSize: 11 }}>{v}</code>, width: 120 },
            { title: 'Employee', dataIndex: 'employee_name', render: (v: string, r: any) => r.is_anonymous ? <Tag>Anonymous</Tag> : <>{v}</> },
            { title: 'Type', dataIndex: 'grievance_type', render: (v: string) => <Tag color="orange">{v?.replace(/_/g,' ')}</Tag> },
            { title: 'Against', dataIndex: 'against_name', render: (v: string) => v || '-' },
            { title: 'Assigned To', dataIndex: 'assigned_name', render: (v: string) => v || 'Unassigned' },
            { title: 'Filed', dataIndex: 'created_at', render: (v: string) => v?.split('T')[0] },
            { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag color={grievanceColors[v] || 'default'}>{v?.replace(/_/g,' ')}</Tag> },
            { title: 'Actions', render: (_: any, r: any) => (
              <Space>
                <Button size="small" icon={<EyeOutlined />} onClick={() => { setSelected({ ...r, _type: 'grievance' }); setDetailDrawer(true); }} />
                {r.status === 'filed' && <Button size="small" onClick={() => updateStatus(r.id, 'acknowledged', 'grievance')}>Acknowledge</Button>}
                {r.status === 'acknowledged' && <Button size="small" onClick={() => updateStatus(r.id, 'under_investigation', 'grievance')}>Investigate</Button>}
                {r.status === 'under_investigation' && <Button size="small" type="primary" onClick={() => updateStatus(r.id, 'resolved', 'grievance')}>Resolve</Button>}
              </Space>
            )},
          ]} />
      </Card>
    </div>
  );

  return (
    <div style={{ padding: 24 }}>
      <h1>Disciplinary & Grievance Management</h1>
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
        { key: 'disciplinary', label: '⚠️ Disciplinary Cases', children: disciplinaryTab },
        { key: 'grievance', label: '📋 Grievances', children: grievanceTab },
      ]} />

      {/* Disciplinary Case Modal */}
      <Modal title="New Disciplinary Case" open={caseModal} onCancel={() => { setCaseModal(false); caseForm.resetFields(); }} footer={null} width={700}>
        <Alert message="Disciplinary action must follow company policy and Nigerian Labour Law. Ensure documentation is complete." type="warning" showIcon style={{ marginBottom: 16 }} />
        <Form form={caseForm} layout="vertical" onFinish={handleCaseSubmit}>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}><Select showSearch optionFilterProp="children">{empOptions}</Select></Form.Item></Col>
            <Col span={12}><Form.Item name="manager_id" label="Reporting Manager"><Select showSearch optionFilterProp="children" allowClear>{empOptions}</Select></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="incident_type" label="Incident Type" rules={[{ required: true }]}>
              <Select>{['verbal_warning','written_warning','final_warning','suspension','termination','misconduct','poor_performance','attendance','insubordination','other'].map(t => <Select.Option key={t} value={t}>{t.replace(/_/g,' ')}</Select.Option>)}</Select>
            </Form.Item></Col>
            <Col span={12}><Form.Item name="action_taken" label="Action Taken" rules={[{ required: true }]}>
              <Select>{['verbal_warning','written_warning','final_warning','suspension','termination','no_action'].map(t => <Select.Option key={t} value={t}>{t.replace(/_/g,' ')}</Select.Option>)}</Select>
            </Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}><Form.Item name="incident_date" label="Incident Date" rules={[{ required: true }]}><Input type="date" /></Form.Item></Col>
            <Col span={8}><Form.Item name="hearing_date" label="Hearing Date"><Input type="date" /></Form.Item></Col>
            <Col span={8}><Form.Item name="suspension_days" label="Suspension Days (if applicable)"><Input type="number" min={0} /></Form.Item></Col>
          </Row>
          <Form.Item name="description" label="Incident Description" rules={[{ required: true }]}><TextArea rows={4} /></Form.Item>
          <Form.Item name="witnesses" label="Witnesses"><Input placeholder="Names of witnesses" /></Form.Item>
          <Form.Item name="evidence" label="Evidence / Documentation"><TextArea rows={2} /></Form.Item>
          <Form.Item name="outcome" label="Expected Outcome"><TextArea rows={2} /></Form.Item>
          <Space><Button type="primary" htmlType="submit">Create Case</Button><Button onClick={() => setCaseModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>

      {/* Grievance Modal */}
      <Modal title="File Grievance" open={grievanceModal} onCancel={() => { setGrievanceModal(false); grievanceForm.resetFields(); }} footer={null} width={650}>
        <Alert message="Grievances are treated with strict confidentiality. Retaliation against anyone filing a grievance is prohibited." type="info" showIcon style={{ marginBottom: 16 }} />
        <Form form={grievanceForm} layout="vertical" onFinish={handleGrievanceSubmit}>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="employee_id" label="Filing Employee" rules={[{ required: true }]}><Select showSearch optionFilterProp="children">{empOptions}</Select></Form.Item></Col>
            <Col span={12}><Form.Item name="grievance_type" label="Grievance Type" rules={[{ required: true }]}>
              <Select>{['harassment','discrimination','unfair_treatment','workplace_safety','pay_dispute','working_conditions','bullying','other'].map(t => <Select.Option key={t} value={t}>{t.replace(/_/g,' ')}</Select.Option>)}</Select>
            </Form.Item></Col>
          </Row>
          <Form.Item name="against_employee_id" label="Grievance Against (if applicable)"><Select showSearch optionFilterProp="children" allowClear>{empOptions}</Select></Form.Item>
          <Form.Item name="assigned_to" label="Assign to HR Officer"><Select showSearch optionFilterProp="children" allowClear>{empOptions}</Select></Form.Item>
          <Form.Item name="description" label="Detailed Description" rules={[{ required: true }]}><TextArea rows={5} /></Form.Item>
          <Form.Item name="desired_outcome" label="Desired Outcome"><TextArea rows={2} /></Form.Item>
          <Form.Item name="witnesses" label="Witnesses"><Input /></Form.Item>
          <Form.Item name="is_anonymous" label="File Anonymously" initialValue={0}><Select><Select.Option value={0}>No — Include my name</Select.Option><Select.Option value={1}>Yes — Keep anonymous</Select.Option></Select></Form.Item>
          <Space><Button type="primary" htmlType="submit">File Grievance</Button><Button onClick={() => setGrievanceModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>

      {/* Detail Drawer */}
      <Drawer title={selected?._type === 'disciplinary' ? `Case: ${selected?.case_id}` : `Grievance: ${selected?.grievance_id}`} open={detailDrawer} onClose={() => setDetailDrawer(false)} width={500}>
        {selected && (
          <div>
            <Divider>{selected._type === 'disciplinary' ? 'Disciplinary Case' : 'Grievance'}</Divider>
            <p><strong>Employee:</strong> {selected.employee_name}</p>
            <p><strong>Department:</strong> {selected.department}</p>
            {selected._type === 'disciplinary' ? (
              <>
                <p><strong>Incident Type:</strong> <Tag color={incidentColors[selected.incident_type]}>{selected.incident_type?.replace(/_/g,' ')}</Tag></p>
                <p><strong>Action Taken:</strong> <Tag>{selected.action_taken?.replace(/_/g,' ')}</Tag></p>
                <p><strong>Incident Date:</strong> {selected.incident_date}</p>
                <p><strong>Hearing Date:</strong> {selected.hearing_date || 'Not scheduled'}</p>
                {selected.suspension_days > 0 && <p><strong>Suspension Days:</strong> {selected.suspension_days}</p>}
                <Divider>Description</Divider>
                <p>{selected.description}</p>
                {selected.witnesses && <><Divider>Witnesses</Divider><p>{selected.witnesses}</p></>}
                {selected.outcome && <><Divider>Outcome</Divider><p>{selected.outcome}</p></>}
                {selected.appeal_status !== 'none' && <><Divider>Appeal</Divider><Tag>{selected.appeal_status}</Tag><p>{selected.appeal_notes}</p></>}
              </>
            ) : (
              <>
                <p><strong>Type:</strong> <Tag color="orange">{selected.grievance_type?.replace(/_/g,' ')}</Tag></p>
                {selected.against_name && <p><strong>Against:</strong> {selected.against_name}</p>}
                <p><strong>Anonymous:</strong> {selected.is_anonymous ? 'Yes' : 'No'}</p>
                <Divider>Description</Divider>
                <p>{selected.description}</p>
                <p><strong>Desired Outcome:</strong> {selected.desired_outcome}</p>
                {selected.investigation_notes && <><Divider>Investigation Notes</Divider><p>{selected.investigation_notes}</p></>}
                {selected.resolution && <><Divider>Resolution</Divider><p>{selected.resolution}</p></>}
              </>
            )}
            <Divider />
            <Tag color={selected._type === 'disciplinary' ? statusColors[selected.status] : grievanceColors[selected.status]}>{selected.status?.replace(/_/g,' ').toUpperCase()}</Tag>
          </div>
        )}
      </Drawer>
    </div>
  );
};
export default DisciplinaryManagement;
